import { AsyncPipe, NgClass, TitleCasePipe } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, signal } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, combineLatest, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, takeUntil, withLatestFrom } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { ClarityService } from "src/app/services/clarity.service";
import { StoreRepositoryService } from "../store-repository.service";
import { DisplayCurrencyPipe } from "../../../pipe/display-currency.pipe";
import { AmountPipe } from "./amount-pipe";

@Component({
  selector: "app-checkout",
  templateUrl: "./checkout.component.html",
  styleUrls: ["./checkout.component.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [
    AmountPipe,
    IonicModule,
    NgClass,
    AsyncPipe,
    DisplayCurrencyPipe,
    TitleCasePipe,
    TranslatePipe],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  selectedLanguage: string = "en";

  selectedProduct$ = this.storeService.selectedProduct$;
  private selectedDay$ = new BehaviorSubject([]);
  private selectedLine$ = new BehaviorSubject(0);
  private languageSubscription: Subscription;
  discount$ = new BehaviorSubject(null);
  storeOffers = [];
  productOffers$ = new BehaviorSubject(null);
  offers;

  PromoCreditBackSignal = signal<string>(null);
  private destroy = new Subject<void>();
  selectedOfferPrices$ = this.discount$.pipe(
    withLatestFrom(this.selectedDay$, this.selectedLine$, this.productOffers$),

    map(([discount, selectedDay, selectedLine, productOffers]) => {
      if (discount && selectedDay && selectedDay?.length && productOffers) {
        var offers = productOffers[`${discount?.type}`]?.find((x: any) => {
          let draw_day = [];
          if (!x || !x.draw_day) return;
          x?.draw_day?.forEach((val) => draw_day?.push(val));
          return (
            draw_day?.sort().toString().trim() === selectedDay?.sort().toString().trim() &&
            x.lines === selectedLine + 1 &&
            x.payment_type === "cash"
          );
        });
        this.changeDetectorRef.detectChanges();
        this.offers = offers;
        return offers;
      }
    })
  );
  spinnerAnimation: boolean;
  progressBar: number;
  proceed = true;
  timer$: any;
  sub: any;
  preferredCurrency: string = CommonVariable.preferredCurrency;
  CurrencyDetailsSignal = signal<any>([]);
  preferredCurrencySignal = signal<string>(CommonVariable.preferredCurrency);
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  currencySymbolSignal = signal<string>(CommonVariable.defaultCurrencySymbol);
  public languageCode: string;

  public languageCodeSignal = signal<string>("");
  public offerDays;
  public initialLines;
  userId: string;
  constructor(
    public bottomSheet: MatBottomSheetRef<CheckoutComponent>,
    private storeService: StoreRepositoryService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private analytics: AngularFireAnalytics,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService,
    private GTMSagmentService: GTMSagmentService,
    private mixPanelService: MixpanelService,
    private clarityService: ClarityService,
    public translate: TranslateService
  ) {
    this.languageSubscription = this.authService.selectedlanguage$.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : "en";
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );
    this.mixPanelService.GTMPopupInitializationForInboxNew("Extend and Save Popup Viewed", data?.eventData);
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    if (!data || !data?.storeOffers || !data?.storeOffers?.cash_offer_discount) return;
    this.offerDays = this.data?.selectedDay?.length ?? 1;
    this.initialLines = this.data?.selectedLine + 1;
    this.productOffers$.next(data?.productOffers);
    this.selectedDay$.next(data?.selectedDay);
    this.selectedLine$.next(data?.selectedLine);
    this.storeOffers = data?.storeOffers?.cash_offer_discount;
    this.discount$.next(data?.selectedDiscount);
    // this.analytics.logEvent("product_checkout_modal", { userId: this.userId });
    // Subscribe to userDetails$ observable
    this.authService?.userDetails$?.subscribe({
      next: (user) => {
        // Set preferred currency based on user data or default to CommonVariable.preferredCurrency
        this.preferredCurrencySignal.set(user?.preferred_currency || CommonVariable.preferredCurrency);
        this.preferredCurrency = this.preferredCurrencySignal();
        // Set language code based on user data or default to 'en'
        this.languageCodeSignal.set(user?.language_code || "en");
        this.languageCode = this.languageCodeSignal();
        // Subscribe to getCurrencyList$ observable
        this.authService.getCurrencyList$?.subscribe({
          next: (data: Data) => {
            // Find currency details based on preferred currency
            this.CurrencyDetailsSignal.set(data?.details?.find((x) => x?.currency_code === this.preferredCurrency));
            const CurrencyDetails = this.CurrencyDetailsSignal();
            // Set currency symbol based on currency details or default to CommonVariable.defaultCurrencySymbol
            this.currencySymbolSignal.set(CurrencyDetails?.currency_symbol || CommonVariable.defaultCurrencySymbol);
            this.currencySymbol = this.currencySymbolSignal();
          },
          error: (err) => {
            return err;
          },
        });
      },
      error: (err) => {
        return err;
      },
    });
    this.getCreditBackAmount();
  }
  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
    this.destroy.next();
    this.destroy.complete();
  }

  async ngOnInit() {
    // Fetch user ID from AuthService
    await this.authService
      .getUid("CheckoutComponent")
      .then((uid) => {
        this.userId = uid;
         this.analytics.logEvent("product_checkout_modal", { userId: this.userId });
      })
      .catch((error: any) => {
        return error;
      });
  }

  changeDiscount(discount) {
    this.discount$.next(discount);
  }

  selectOption(discount: any) {
    if (discount === "noExtension") {
      this.discount$.next("No Extension");
      return;
    }
    this.discount$.next(discount);
  }

  checkOut() {
    this.clarityService.event("begin_checkout");
    this.clarityService.setTag("journey_step", "checkout");
    this.clarityService.setTag("product_type", "syndicate");

    let offersData = {
      selectedDiscount: this.discount$?.value,
      selectedDay: this.selectedDay$?.value,
      selectedLine: this.selectedLine$?.value,
      offers: this.offers,
      discount_available:this.storeService.iSdiscountAvailable()
    };
    this.bottomSheet.dismiss({ ...offersData, ...{ checkout: true } });
  }

  closeDialog() {
    this.bottomSheet.dismiss({ discount: this.discount$.value });
  }

  getCreditBackAmount() {
    combineLatest([this.selectedDay$, this.selectedLine$, this.productOffers$]).pipe(
      map(([selectedDay, selectedLine, productOffers]) => {
        const offers = productOffers['8_weeks'];
        if (!offers) return null;

        const match = offers.find((offer : Data) =>
          offer.lines === selectedLine + 1 &&
          offer?.draw_day?.sort().toString().trim() === selectedDay?.sort().toString().trim()
        );
        return match?.credit_back_amount ?? null;
      }),
      distinctUntilChanged(),
      filter(creditBackAmount => creditBackAmount !== null),
      takeUntil(this.destroy)
    ).subscribe(creditBackAmount => {
      if (!creditBackAmount) {
        this.PromoCreditBackSignal.set(null);
        return;
      }

      this.authService.getDVFromString(creditBackAmount).then((res: string) => {
        this.PromoCreditBackSignal.set(res);
      }).catch(() => {
        this.PromoCreditBackSignal.set(null);
      });
    });
  }
}
